import { MouseEvent as ReactMouseEvent, ReactElement } from 'react';
import cn from 'classnames';

import { useLocale } from '@hooks/useLocale';
import { addLocaleToUrl } from '@utils/url';
import {
  ABSOLUTE_LINK_PATTERN,
  LOCALE_PREFIX_PATTERN,
} from '@utils/breadcrumbs/withLocalePrefix';
import { isModifiedClick } from '_utils/GA4/clickTracking';
import { trackHotspotCtaClick } from '_utils/GA4/trackHotspotEvents';

import styles from './CtaLinks.module.scss';

export interface CtaLink {
  label: string;
  url: string;
}

export interface CtaLinksProps {
  ctas: CtaLink[];
  className?: string;
  /** Context for `click_hotspot_cta` (ED-416), which this component fires
      itself since it owns the links. */
  interactionLocation: string;
  tabName: string;
  coOpBrand: string | null;
}

/**
 * Mirrors `_components/MainHero/index.tsx`'s `localizeHref`: absolute links
 * (`https:`, `mailto:`, `tel:`, protocol-relative `//`) and fragments are left
 * untouched, and a url that already carries a locale segment (e.g. `/en/camping`)
 * isn't re-prefixed — only a bare relative path gets `addLocaleToUrl`'d.
 */
const buildCtaHref = (raw: string, locale: string): string => {
  if (ABSOLUTE_LINK_PATTERN.test(raw)) return raw;
  const [path] = raw.split(/[?#]/);
  return LOCALE_PREFIX_PATTERN.test(path) ? raw : addLocaleToUrl(raw, locale);
};

/**
 * White, underlined CTA links over an image (Figma's "Link" component), shared
 * by `ImageHotspot`'s `ReviewPanel` and `ImageProductStrip`. Every click fires
 * `click_hotspot_cta`.
 */
const CtaLinks = ({
  ctas,
  className,
  interactionLocation,
  tabName,
  coOpBrand,
}: CtaLinksProps): ReactElement | null => {
  const locale = useLocale();

  if (ctas.length === 0) return null;

  return (
    <div className={cn(styles.ctaGroup, className)}>
      {ctas.map((cta, index) => {
        const href = buildCtaHref(cta.url, locale);

        // ED-416 (4): a real `<a href>` navigation (like
        // `PlpCrossLinkingCard`'s own cards), so a modified click (opens in a
        // new tab) keeps this page alive and fires normally, while a plain
        // click defers navigation until the hit is sent (or GTM times out).
        const handleClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
          const data = {
            interactionLocation,
            linkUrl: href,
            linkText: cta.label,
            tabName,
            coOpBrand,
            index,
          };

          if (isModifiedClick(event)) {
            trackHotspotCtaClick({ data });
            return;
          }

          event.preventDefault();
          trackHotspotCtaClick({
            data,
            eventCallback: () => {
              window.location.href = href;
            },
          });
        };

        return (
          <a
            // `cta.url` alone isn't guaranteed unique — two CTAs in the same
            // tab could plausibly point at the same URL (e.g. both linking
            // to the same collection). Combined with `index` instead.
            // eslint-disable-next-line react/no-array-index-key -- `index` is combined with `cta.url` above, not used bare, so it stays unique even when two CTAs do share a URL.
            key={`${cta.url}-${index}`}
            href={href}
            onClick={handleClick}
            className={styles.ctaLink}
          >
            {cta.label}
            <span className={styles.ctaUnderline} aria-hidden="true" />
          </a>
        );
      })}
    </div>
  );
};

export default CtaLinks;
